import React, { useCallback, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import type { CategoryPlan } from '@app/src/types/categoryPlan'
import {
  groupByCategory,
  toggleCategory,
  renameCategoryName,
  removeOption,
  restoreOption,
  renameOption,
  moveOption,
  getAllCategoryKeys,
  setReplaceMode,
} from '../categoryPlanLogic'
import CategoryCard from './CategoryCard'

type CategoryMappingsSectionProps = {
  plan: CategoryPlan
  existingCategoryNames: Record<string, string> | null
  onChange: (plan: CategoryPlan) => void
  onConvertToColumns: (sourceColumnNames: string[]) => void
}

const CategoryMappingsSection: React.FC<CategoryMappingsSectionProps> = ({
  plan,
  existingCategoryNames,
  onChange,
  onConvertToColumns,
}) => {
  const groups = useMemo(() => groupByCategory(plan), [plan])
  const allCategoryKeys = useMemo(() => getAllCategoryKeys(plan), [plan])

  const handleToggle = useCallback(
    (categoryKey: string) => {
      onChange(toggleCategory(plan, categoryKey))
    },
    [plan, onChange],
  )

  if (groups.length === 0) return null

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Category mappings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We detected {groups.length} categor{groups.length === 1 ? 'y' : 'ies'} in your data. Review the mappings below
        and disable any that don&apos;t look right.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {groups.map(group => {
          const key =
            group.existingCategoryId != null
              ? `existing:${group.existingCategoryId}`
              : `new:${group.suggestedCategoryName}`
          const defaultExpanded = group.existingCategoryId == null || group.confidenceScore < 0.85

          const resolvedExistingName =
            group.existingCategoryId != null
              ? (existingCategoryNames?.[String(group.existingCategoryId)] ?? null)
              : null

          return (
            <CategoryCard
              key={key}
              group={group}
              categoryKey={key}
              allCategoryKeys={allCategoryKeys}
              existingCategoryName={resolvedExistingName}
              defaultExpanded={defaultExpanded}
              onToggle={() => handleToggle(key)}
              onRenameCategoryName={newName => onChange(renameCategoryName(plan, key, newName))}
              onRenameOption={(rawValue, newName) => onChange(renameOption(plan, rawValue, newName, key))}
              onMoveOption={(rawValue, targetKey) => onChange(moveOption(plan, rawValue, key, targetKey))}
              onRemoveOption={rawValue => onChange(removeOption(plan, rawValue, key))}
              onRestoreOption={rawValue => onChange(restoreOption(plan, rawValue, key))}
              onSetReplaceMode={replaceMode => onChange(setReplaceMode(plan, key, replaceMode))}
              onConvertToColumns={() => onConvertToColumns(group.sourceColumns)}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export default CategoryMappingsSection
